import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Clock,
  User,
  FileText,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';
import { useAuditHistory, useAuditSnapshot } from '../../hooks/useAuditHistory';
import DiffViewer, { StatusChangeDiff } from './DiffViewer';

/**
 * Get icon and color for an action type.
 */
const getActionStyle = (action) => {
  switch (action) {
    case 'create':
      return {
        icon: <Plus className="w-5 h-5" />,
        color: 'bg-green-500',
        label: 'Created'
      };
    case 'update':
      return {
        icon: <Edit className="w-5 h-5" />,
        color: 'bg-blue-500',
        label: 'Updated'
      };
    case 'delete':
      return {
        icon: <Trash2 className="w-5 h-5" />,
        color: 'bg-red-500',
        label: 'Deleted'
      };
    case 'status_change':
      return {
        icon: <ArrowRight className="w-5 h-5" />,
        color: 'bg-orange-500',
        label: 'Status Changed'
      };
    default:
      return {
        icon: <FileText className="w-5 h-5" />,
        color: 'bg-gray-500',
        label: action
      };
  }
};

/**
 * Format timestamp for display.
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString();
  } catch {
    return timestamp;
  }
};

/**
 * Single timeline entry component.
 */
const TimelineEntry = ({ entry, entityType, entityId, onViewSnapshot }) => {
  const [expanded, setExpanded] = useState(false);
  const actionStyle = getActionStyle(entry.action);

  const hasChanges = entry.details?.changes && Object.keys(entry.details.changes).length > 0;
  const hasStatusChange = entry.details?.status_change;
  const hasSnapshot = entry.details?.snapshot;
  const changeCount = hasChanges ? Object.keys(entry.details.changes).length : 0;

  return (
    <div className="relative flex gap-4 items-start">
      {/* Icon */}
      <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full ${actionStyle.color} text-white shadow-lg flex-shrink-0`}>
        {actionStyle.icon}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-bold text-gray-900">
                  {actionStyle.label}
                </h4>
                {hasStatusChange && (
                  <p className="text-sm text-gray-600 mt-1">
                    {entry.details.status_change.old_status} → {entry.details.status_change.new_status}
                  </p>
                )}
                {hasChanges && !hasStatusChange && (
                  <p className="text-sm text-gray-600 mt-1">
                    {changeCount} field{changeCount !== 1 ? 's' : ''} changed
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {(hasChanges || hasStatusChange || hasSnapshot) && (
                  <button className="text-gray-400 hover:text-gray-600">
                    {expanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Actor and timestamp */}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                <span className="font-medium">{entry.actor?.email || 'Unknown'}</span>
                {entry.actor?.role && (
                  <span className="text-gray-400">({entry.actor.role})</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{formatTimestamp(entry.timestamp)}</span>
              </div>
            </div>
          </div>

          {/* Expanded Content */}
          {expanded && (
            <div className="border-t border-gray-100 p-4 bg-gray-50">
              {/* Status Change */}
              {hasStatusChange && (
                <div className="mb-4">
                  <StatusChangeDiff
                    oldStatus={entry.details.status_change.old_status}
                    newStatus={entry.details.status_change.new_status}
                    reason={entry.details.status_change.reason}
                  />
                </div>
              )}

              {/* Field Changes */}
              {hasChanges && (
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Changes</h5>
                  <DiffViewer changes={entry.details.changes} />
                </div>
              )}

              {/* View Snapshot Button */}
              {hasSnapshot && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewSnapshot(entry.id);
                  }}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Eye className="w-4 h-4" />
                  View Document State
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Snapshot Modal Component.
 */
const SnapshotModal = ({ snapshot, onClose }) => {
  if (!snapshot) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">
            Document State at {formatTimestamp(snapshot.timestamp)}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div className="text-sm text-gray-600 mb-4">
            <span className="font-medium">Action:</span> {snapshot.action}
            <span className="mx-2">|</span>
            <span className="font-medium">By:</span> {snapshot.actor?.email}
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
              {JSON.stringify(snapshot.snapshot, null, 2)}
            </pre>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * AuditTrailViewer Component
 *
 * Main component for displaying entity audit history in a timeline format.
 *
 * @param {Object} props
 * @param {string} props.entityType - Type of entity (hazard, risk_assessment, etc.)
 * @param {string} props.entityId - ID of the entity
 * @param {string} props.title - Optional custom title
 */
const AuditTrailViewer = ({ entityType, entityId, title = 'Audit History' }) => {
  const {
    history,
    loading,
    error,
    totalEntries,
    hasMore,
    loadMore,
    refresh
  } = useAuditHistory(entityType, entityId, { limit: 20 });

  const {
    snapshot,
    loading: snapshotLoading,
    fetchSnapshot,
    clearSnapshot
  } = useAuditSnapshot(entityType, entityId);

  const handleViewSnapshot = async (auditId) => {
    await fetchSnapshot(auditId);
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            Error Loading Audit History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={refresh}
            className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-800"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {title}
            {totalEntries > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({totalEntries} entries)
              </span>
            )}
          </div>

          <button
            onClick={refresh}
            disabled={loading}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {loading && history.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading audit history...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No audit history available</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

            {/* Timeline entries */}
            <div className="space-y-4">
              {history.map((entry) => (
                <TimelineEntry
                  key={entry.id}
                  entry={entry}
                  entityType={entityType}
                  entityId={entityId}
                  onViewSnapshot={handleViewSnapshot}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 inline-block mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Snapshot Modal */}
      {snapshot && (
        <SnapshotModal
          snapshot={snapshot}
          onClose={clearSnapshot}
        />
      )}

      {/* Loading overlay for snapshot */}
      {snapshotLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-4 shadow-lg">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        </div>
      )}
    </Card>
  );
};

export default AuditTrailViewer;
